import { type CSSProperties, type MouseEvent, type MouseEventHandler, type ReactNode, type Ref, useState } from 'react';

import classNames from 'classnames';
import type { JSX } from 'react';

import { Spinner } from '../Spinner/Spinner';

import './Button.less';

export type ButtonType = 'primary' | 'link' | 'default' | 'danger' | 'ghost' | 'unstyled';

export type ButtonProps = {
  type?: ButtonType;
  disabled?: boolean;
  className?: string;
  htmlType?: 'button' | 'reset' | 'submit';
  onClick?: MouseEventHandler<HTMLButtonElement>;
  icon?: ReactNode;
  loadingIcon?: ReactNode;
  children?: ReactNode;
  loading?: boolean;
  size?: 'small' | 'large';
  style?: CSSProperties;
  ariaLabel?: string;
  hasPairedInput?: boolean;
  value?: string;
  ref?: Ref<HTMLButtonElement>;
  cooldownMs?: number;
};

const propToClassName = (propValue: string): string => `bw-button-${propValue}`;

export const Button = (buttonProps: ButtonProps): JSX.Element => {
  const {
    children,
    onClick,
    icon,
    type = 'default',
    loading,
    loadingIcon,
    size,
    className,
    htmlType = 'button',
    disabled = false,
    style,
    ariaLabel,
    hasPairedInput,
    value,
    ref,
    cooldownMs,
    ...rest
  } = buttonProps;

  const [disabledByCooldown, setDisabledByCooldown] = useState(false);

  const handleClick = (e: MouseEvent<HTMLButtonElement>): void => {
    if (disabled || loading) {
      e.preventDefault();
      return;
    }

    if (cooldownMs) {
      setDisabledByCooldown(true);
      setTimeout(() => {
        setDisabledByCooldown(false);
      }, cooldownMs);
    }

    onClick?.(e);
  };

  return (
    <button
      onClick={handleClick}
      className={classNames(
        'bw-button',
        loading && 'bw-button-loading',
        hasPairedInput && 'bw-button-has-paired-input',
        size && propToClassName(size),
        propToClassName(type),
        className,
      )}
      value={value}
      style={style}
      type={htmlType}
      disabled={disabledByCooldown || disabled}
      aria-label={ariaLabel}
      {...rest}
      ref={ref}
    >
      {loading ? <Spinner size="small" icon={loadingIcon} /> : null}
      {icon && !loading ? icon : null}
      {(icon || loading) && children ? <span style={{ marginLeft: '10px' }}>{children}</span> : children}
    </button>
  );
};
